/**
 * ====================================================================
 * FIKO CAD BASELINE DIMENSION
 * ====================================================================
 *
 * Baseline ölçü - Ortak referans noktasından tüm diğer noktalara mesafe.
 * CATIA/AutoCAD'in "Baseline Dimension" özelliğine karşılık gelir.
 *
 * Özellikler:
 * - Tek referans noktası (datum)
 * - Paralel ölçü çizgileri
 * - Kademeli offset
 * - Sıralı ölçü değerleri
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    BaseDimensionOptions,
    AxisLock,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// BASELINE DIMENSION OPTIONS
// ====================================================================

/**
 * Baseline dimension için özel seçenekler
 */
export interface BaselineDimensionOptions extends BaseDimensionOptions {
    /** Eksen kilidi */
    axisLock?: AxisLock;
    /** Kademeler arası offset */
    levelOffset?: number;
    /** İlk ölçü offset'i */
    firstOffset?: number;
    /** Datum işareti göster */
    showDatumMarker?: boolean;
    /** Datum işareti boyutu */
    datumMarkerSize?: number;
}

/**
 * Baseline segment bilgisi
 */
export interface BaselineSegment {
    point: THREE.Vector3;
    distance: number;
    level: number;          // Kademesi (0, 1, 2, ...)
    index: number;
}

// ====================================================================
// BASELINE DIMENSION CLASS
// ====================================================================

/**
 * Baseline Dimension - Baseline ölçü
 */
export class BaselineDimension extends Dimension {
    // Datum (referans) noktası
    private datumPoint: THREE.Vector3;

    // Ölçülecek noktalar
    private targetPoints: THREE.Vector3[] = [];
    private segments: BaselineSegment[] = [];

    // Çizim elemanları
    private dimensionLines: LineSegments2[] = [];
    private extLines: LineSegments2[] = [];
    private arrowMeshes: THREE.Mesh[] = [];
    private labels: THREE.Sprite[] = [];
    private datumMarker: THREE.Group | null = null;

    // Pre-allocated
    private readonly _tempVec = new THREE.Vector3();
    private readonly _direction = new THREE.Vector3();
    private readonly _offsetDir = new THREE.Vector3();

    // Shared geometries
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // SABİT BOYUT
    sabitBoyut = true;
    private hazirlaListenerEklendi = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // PERFORMANS
    private lastKameraUpdateTime = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS = 100;
    private targetScaleAdjustment = 1;
    private currentScaleAdjustment = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        datumPoint: THREE.Vector3,
        targetPoints: THREE.Vector3[],
        options: BaselineDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.BASELINE, options);

        this.datumPoint = datumPoint.clone();
        this.scene = scene;

        // Hedef noktaları kopyala
        for (const p of targetPoints) {
            this.targetPoints.push(p.clone());
        }
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Minimum 1 hedef nokta gerekli
            if (this.targetPoints.length < 1) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'En az 1 hedef nokta gerekli'
                    }
                };
            }

            // Segmentleri hesapla
            this.calculateSegments();

            // Toplam değer (en uzak mesafe)
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.COINCIDENT_POINTS,
                        message: 'Noktalar çakışıyor'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Baseline dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Segmentleri hesapla
     */
    private calculateSegments(): void {
        this.segments = [];

        const options = this._options as BaselineDimensionOptions;
        const axisLock = options.axisLock || AxisLock.NONE;

        // Mesafeleri hesapla ve sırala
        const unsortedSegments: BaselineSegment[] = [];

        for (let i = 0; i < this.targetPoints.length; i++) {
            const point = this.targetPoints[i];
            let distance: number;

            switch (axisLock) {
                case AxisLock.X:
                    distance = Math.abs(point.x - this.datumPoint.x);
                    break;
                case AxisLock.Y:
                    distance = Math.abs(point.y - this.datumPoint.y);
                    break;
                case AxisLock.Z:
                    distance = Math.abs(point.z - this.datumPoint.z);
                    break;
                case AxisLock.XY_PLANE:
                    distance = Math.sqrt(
                        Math.pow(point.x - this.datumPoint.x, 2) +
                        Math.pow(point.y - this.datumPoint.y, 2)
                    );
                    break;
                case AxisLock.XZ_PLANE:
                    distance = Math.sqrt(
                        Math.pow(point.x - this.datumPoint.x, 2) +
                        Math.pow(point.z - this.datumPoint.z, 2)
                    );
                    break;
                case AxisLock.YZ_PLANE:
                    distance = Math.sqrt(
                        Math.pow(point.y - this.datumPoint.y, 2) +
                        Math.pow(point.z - this.datumPoint.z, 2)
                    );
                    break;
                default:
                    distance = this.datumPoint.distanceTo(point);
            }

            unsortedSegments.push({
                point: point.clone(),
                distance,
                level: 0,  // Sonra atanacak
                index: i
            });
        }

        // Mesafeye göre sırala
        unsortedSegments.sort((a, b) => a.distance - b.distance);

        // Level ata
        for (let i = 0; i < unsortedSegments.length; i++) {
            unsortedSegments[i].level = i;
        }

        this.segments = unsortedSegments;
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * Sahneye hazırlama
     */
    hazirla(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        try {
            if (this.labels.length > 0 && this.labels[0]?.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Sessizce devam et
        }
    }

    /**
     * Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) return;

        if (now - this.lastKameraUpdateTime < BaselineDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, BaselineDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * Kamera değişikliğinde scale güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        if (this.sabitBoyut) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * Smooth scale interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.sabitBoyut || this.labels.length === 0) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            for (const label of this.labels) {
                if (label?.userData.originalScale) {
                    const originalScale = label.userData.originalScale as THREE.Vector3;
                    label.scale.set(
                        originalScale.x * this.currentScaleAdjustment,
                        originalScale.y * this.currentScaleAdjustment,
                        1
                    );
                }
            }

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this.segments.some(s => s.distance >= DIMENSION_CONSTANTS.MIN_DISTANCE);
    }

    /**
     * Değeri hesapla (en uzak mesafe)
     */
    protected calculateValue(): number {
        if (this.segments.length === 0) return 0;
        return Math.max(...this.segments.map(s => s.distance));
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as BaselineDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const firstOffset = options.firstOffset || DIMENSION_CONSTANTS.DEFAULT_OFFSET;
        const levelOffset = options.levelOffset || 5;
        const showDatumMarker = options.showDatumMarker !== false;
        const datumMarkerSize = options.datumMarkerSize || 1;
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        if (this.segments.length === 0) return;

        // Genel yön hesapla (datum'dan en uzak noktaya)
        const farthestSegment = this.segments[this.segments.length - 1];
        this._direction.subVectors(farthestSegment.point, this.datumPoint).normalize();

        // Offset yönü (dimension çizgilerine dik)
        this._offsetDir.set(0, 1, 0);
        if (Math.abs(this._direction.dot(this._offsetDir)) > 0.9) {
            this._offsetDir.set(1, 0, 0);
        }
        this._offsetDir.crossVectors(this._direction, this._offsetDir).normalize();

        // Datum marker
        if (showDatumMarker) {
            this.datumMarker = this.createDatumMarker(this.datumPoint, color, datumMarkerSize);
            this.add(this.datumMarker);
        }

        // Her segment için dimension çiz
        for (const segment of this.segments) {
            const levelOffsetTotal = firstOffset + (segment.level * levelOffset);

            // Dimension line pozisyonları
            const dimStart = this.datumPoint.clone()
                .addScaledVector(this._offsetDir, levelOffsetTotal);
            const dimEnd = segment.point.clone()
                .addScaledVector(this._offsetDir, levelOffsetTotal);

            // Dimension line
            const dimLine = this.createLine(
                [dimStart.x, dimStart.y, dimStart.z, dimEnd.x, dimEnd.y, dimEnd.z],
                color, lineWidth
            );
            dimLine.name = `DimLine_${segment.index}`;
            this.dimensionLines.push(dimLine);
            this.add(dimLine);

            // Extension line (datum'dan)
            if (segment.level === 0) {
                const datumExt = this.createLine(
                    [
                        this.datumPoint.x, this.datumPoint.y, this.datumPoint.z,
                        dimStart.x, dimStart.y, dimStart.z
                    ],
                    color, lineWidth * 0.7, 0.5
                );
                datumExt.name = 'DatumExtLine';
                this.extLines.push(datumExt);
                this.add(datumExt);
            }

            // Extension line (hedef noktadan)
            const targetExt = this.createLine(
                [
                    segment.point.x, segment.point.y, segment.point.z,
                    dimEnd.x, dimEnd.y, dimEnd.z
                ],
                color, lineWidth * 0.7, 0.5
            );
            targetExt.name = `ExtLine_${segment.index}`;
            this.extLines.push(targetExt);
            this.add(targetExt);

            // Oklar
            const dimDir = new THREE.Vector3().subVectors(dimEnd, dimStart).normalize();

            // Sadece bitiş noktasında ok (datum'da değil)
            const arrow = this.createArrow(dimEnd, dimDir.clone().negate(), color, arrowSize);
            arrow.name = `Arrow_${segment.index}`;
            this.arrowMeshes.push(arrow);
            this.add(arrow);

            // Datum tarafında da ok
            const datumArrow = this.createArrow(dimStart, dimDir, color, arrowSize);
            datumArrow.name = `DatumArrow_${segment.index}`;
            this.arrowMeshes.push(datumArrow);
            this.add(datumArrow);

            // Label
            const labelPos = dimStart.clone().lerp(dimEnd, 0.5);
            labelPos.addScaledVector(this._offsetDir, options.textOffset || 1);

            const labelText = this.formatValue(segment.distance);
            const label = this.createLabel(labelText, labelPos, options.textColor || '#000000');
            label.name = `Label_${segment.index}`;
            this.labels.push(label);
            this.add(label);
        }
    }

    /**
     * Datum işareti oluştur
     */
    private createDatumMarker(position: THREE.Vector3, color: number, size: number): THREE.Group {
        const group = new THREE.Group();
        group.name = 'DatumMarker';

        // Üçgen datum sembolü
        const shape = new THREE.Shape();
        shape.moveTo(0, size);
        shape.lineTo(-size * 0.6, -size * 0.5);
        shape.lineTo(size * 0.6, -size * 0.5);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateX(-Math.PI / 2);

        const material = new THREE.MeshBasicMaterial({
            color,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.8,
            toneMapped: false
        });

        const marker = new THREE.Mesh(geometry, material);
        marker.position.copy(position);
        marker.renderOrder = Infinity;
        marker.frustumCulled = false;
        group.add(marker);

        return group;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Ok oluştur
     */
    private createArrow(position: THREE.Vector3, direction: THREE.Vector3, color: number, size: number): THREE.Mesh {
        if (!BaselineDimension._sharedArrowGeometry) {
            BaselineDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 8);
            BaselineDimension._sharedArrowGeometry.translate(0, -0.4, 0);
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const arrow = new THREE.Mesh(BaselineDimension._sharedArrowGeometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3, textColor: string): THREE.Sprite {
        const fontSize = this._options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 14;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.roundRect(3, 3, canvas.width - 6, canvas.height - 6, 4);
        ctx.fill();

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = textColor;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.01, canvas.height * 0.01, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        for (const line of this.dimensionLines) {
            (line.material as LineMaterial).color.setHex(color);
        }

        for (const arrow of this.arrowMeshes) {
            (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        for (const line of this.dimensionLines) {
            this.remove(line);
            line.geometry.dispose();
            (line.material as LineMaterial).dispose();
        }
        this.dimensionLines = [];

        for (const line of this.extLines) {
            this.remove(line);
            line.geometry.dispose();
            (line.material as LineMaterial).dispose();
        }
        this.extLines = [];

        for (const arrow of this.arrowMeshes) {
            this.remove(arrow);
            (arrow.material as THREE.Material).dispose();
        }
        this.arrowMeshes = [];

        for (const label of this.labels) {
            this.remove(label);
            label.material.map?.dispose();
            label.material.dispose();
        }
        this.labels = [];

        if (this.datumMarker) {
            this.datumMarker.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    (child.material as THREE.Material)?.dispose();
                }
            });
            this.remove(this.datumMarker);
            this.datumMarker = null;
        }
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Hedef nokta ekle
     */
    addTargetPoint(point: THREE.Vector3): void {
        this.targetPoints.push(point.clone());
        this.calculateSegments();
        this._value = this.calculateValue();
        this.buildGeometry();
    }

    /**
     * Datum noktasını değiştir
     */
    setDatumPoint(point: THREE.Vector3): void {
        this.datumPoint.copy(point);
        this.calculateSegments();
        this._value = this.calculateValue();
        this.buildGeometry();
    }

    /**
     * Datum noktasını al
     */
    getDatumPoint(): THREE.Vector3 {
        return this.datumPoint.clone();
    }

    /**
     * Segment'leri al
     */
    getSegments(): BaselineSegment[] {
        return this.segments.map(s => ({
            ...s,
            point: s.point.clone()
        }));
    }

    /**
     * Segment sayısını al
     */
    getSegmentCount(): number {
        return this.segments.length;
    }

    /**
     * Dispose
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
